import { useState, useCallback, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AuditLog, AuditAction, User } from '../types';

// Generate a unique session ID
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('audit_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('audit_session_id', sessionId);
  }
  return sessionId;
};

// Get client IP (simplified - in production you might want a more robust solution)
const getClientIP = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch {
    return 'unknown';
  }
};

// Standalone audit logger - usable outside React components (e.g. AuthContext login/logout)
export async function logAuditEvent(
  user: { uid: string; email: string },
  action: AuditAction,
  details: Record<string, any> = {}
): Promise<void> {
  try {
    await addDoc(collection(db, 'auditLogs'), {
      userId: user.uid,
      userEmail: user.email,
      action,
      details,
      sessionId: getSessionId(),
      ipAddress: await getClientIP(),
      userAgent: navigator.userAgent,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Failed to log audit event:', action, error);
  }
}

export function useAuditLog() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingLogs, setPendingLogs] = useState<Omit<AuditLog, 'id'>[]>([]);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync pending logs when coming back online
  useEffect(() => {
    if (isOnline && pendingLogs.length > 0) {
      syncPendingLogs();
    }
  }, [isOnline]);

  // Sync pending logs to cloud
  const syncPendingLogs = useCallback(async () => {
    if (pendingLogs.length === 0) return;

    const logsToSync = [...pendingLogs];
    setPendingLogs([]);

    try {
      for (const log of logsToSync) {
        await addDoc(collection(db, 'auditLogs'), {
          ...log,
          timestamp: serverTimestamp()
        });
      }

      // Clear local storage after successful sync
      localStorage.removeItem('audit_pending_logs');
    } catch (error) {
      console.error('Failed to sync audit logs:', error);
      // Put logs back in pending if sync fails
      setPendingLogs(prev => [...prev, ...logsToSync]);
    }
  }, [pendingLogs]);

  // Load pending logs from localStorage on mount
  useEffect(() => {
    const savedLogs = localStorage.getItem('audit_pending_logs');
    if (savedLogs) {
      try {
        const parsed = JSON.parse(savedLogs);
        setPendingLogs(parsed.map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp)
        })));
      } catch (error) {
        console.error('Failed to parse pending audit logs:', error);
        localStorage.removeItem('audit_pending_logs');
      }
    }
  }, []);

  // Save pending logs to localStorage
  useEffect(() => {
    if (pendingLogs.length > 0) {
      localStorage.setItem('audit_pending_logs', JSON.stringify(pendingLogs));
    } else {
      localStorage.removeItem('audit_pending_logs');
    }
  }, [pendingLogs]);

  // Log an action
  const logAction = useCallback(async (
    user: User,
    action: AuditAction,
    details: Record<string, any> = {}
  ) => {
    const sessionId = getSessionId();
    const timestamp = new Date();
    
    const auditLog: Omit<AuditLog, 'id'> = {
      userId: user.uid,
      userEmail: user.email,
      action,
      timestamp,
      details,
      sessionId,
      ipAddress: await getClientIP(),
      userAgent: navigator.userAgent
    };

    if (isOnline) {
      try {
        await addDoc(collection(db, 'auditLogs'), {
          ...auditLog,
          timestamp: serverTimestamp()
        });
      } catch (error) {
        console.error('Failed to log action:', error);
        // Add to pending logs if cloud logging fails
        setPendingLogs(prev => [...prev, auditLog]);
      }
    } else {
      // Store locally when offline
      setPendingLogs(prev => [...prev, auditLog]);
    }
  }, [isOnline]);

  // Get recent audit logs (admin only)
  const getRecentLogs = useCallback(async (limitCount: number = 100) => {
    try {
      const q = query(
        collection(db, 'auditLogs'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      })) as AuditLog[];
    } catch (error) {
      console.error('Failed to get audit logs:', error);
      return [];
    }
  }, []);

  return {
    logAction,
    getRecentLogs,
    isOnline,
    pendingLogsCount: pendingLogs.length
  };
}









